/*
 * Miyo – Auto Gift Box
 *
 * Adds one free Gift Box when the cart crosses the threshold and removes it
 * when the cart drops below. The gift line is always kept at quantity 1.
 *
 * Works together with the cart.js patch (line item key instead of line index),
 * so adding / removing the gift never shifts which item +/- buttons update.
 *
 * Also refreshes the cart drawer when a page is restored with back / forward.
 */
(function () {
  'use strict';

  // ---- Store config -------------------------------------------------------
  // miyokid.com : GIFT_VARIANT_ID = 52320292864301, THRESHOLD = 5199   ($51.99)
  // miyokid.in  : GIFT_VARIANT_ID = <Gift Box variant id from .in admin>,
  //               THRESHOLD = 299900                                    (Rs. 2,999)
  var GIFT_VARIANT_ID = 53836559188152;
  var THRESHOLD = 299900; // in the smallest currency unit (cents / paise)

  var AUTO_FLAG = '_auto_gift';
  var SYNC_DELAY = 500; // wait for Dawn's 300ms quantity debounce to finish

  var nativeFetch = window.fetch.bind(window);
  var syncing = false;
  var syncAgain = false;
  var syncTimer = null;
  var giftUnavailable = false; // set when Shopify refuses the gift variant

  // ---- Helpers ------------------------------------------------------------

  function isGift(item) {
    return item.variant_id === GIFT_VARIANT_ID;
  }

  function isAutoGift(item) {
    return isGift(item) && item.properties && item.properties[AUTO_FLAG];
  }

  // Same number the cart drawer banner and "Estimated total" use: the total
  // after every discount (MIYOFIRST10 too), without the gift itself.
  function eligibleTotal(cart) {
    var giftTotal = 0;

    cart.items.forEach(function (item) {
      if (isGift(item)) giftTotal += item.final_line_price;
    });

    return cart.total_price - giftTotal;
  }

  function cartRequest(url, payload) {
    return nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (response) {
      // 422 on add = the variant doesn't exist / isn't published on this store.
      // Stop retrying on every cart change, it only burns Shopify's rate limit.
      if (response.status === 422 && url === '/cart/add.js') giftUnavailable = true;
      if (!response.ok) throw new Error('Gift box: ' + url + ' failed (' + response.status + ')');
      return response.json();
    });
  }

  // Shopify can split one gift line into two (free + paid) when its quantity
  // goes above 1, so every auto-gift line after the first one is removed.
  function fixGiftLines(cart) {
    var giftLines = cart.items.filter(isAutoGift);
    var updates = {};
    var needsUpdate = false;

    giftLines.forEach(function (item, index) {
      var wanted = index === 0 ? 1 : 0;

      if (item.quantity !== wanted) {
        updates[item.key] = wanted;
        needsUpdate = true;
      }
    });

    return needsUpdate ? cartRequest('/cart/update.js', { updates: updates }) : null;
  }

  function refreshCartUI() {
    var sections = ['cart-drawer', 'cart-icon-bubble'];
    var mainCart = document.getElementById('main-cart-items');

    if (mainCart && mainCart.dataset.id) sections.push(mainCart.dataset.id);

    return nativeFetch('/?sections=' + sections.join(','))
      .then(function (response) {
        return response.json();
      })
      .then(function (html) {
        var parser = new DOMParser();

        swap(parser, html['cart-drawer'], '#CartDrawer .drawer__inner', '.drawer__inner');
        swap(parser, html['cart-icon-bubble'], '#cart-icon-bubble', '.shopify-section');

        if (mainCart) swap(parser, html[mainCart.dataset.id], '#main-cart-items .js-contents', '.js-contents');

        return nativeFetch('/cart.js').then(function (response) {
          return response.json();
        });
      })
      .then(function (cart) {
        var isEmpty = cart.item_count === 0;

        ['cart-drawer', 'cart-drawer-items', '#main-cart-items', '#main-cart-footer'].forEach(function (selector) {
          var element = document.querySelector(selector);
          if (element) element.classList.toggle('is-empty', isEmpty);
        });
      })
      .catch(function () {});
  }

  // The gift must stay at quantity 1, so hide its +/- and remove buttons in
  // the drawer and on /cart. A + on the gift adds a second, paid gift line
  // for a moment before sync() can take it out again.
  function addGiftStyles() {
    if (document.getElementById('gift-box-styles')) return;

    var row = '.cart-item:has([data-quantity-variant-id="' + GIFT_VARIANT_ID + '"])';
    var style = document.createElement('style');

    style.id = 'gift-box-styles';
    style.textContent =
      row + ' quantity-input,\n' +
      row + ' cart-remove-button {\n' +
      '  display: none !important;\n' +
      '}\n\n' +
      row + ' .cart-item__quantity-wrapper::before {\n' +
      '  content: "Qty: 1 \\00B7  Free gift";\n' +
      '  font-size: 1.3rem;\n' +
      '  opacity: 0.75;\n' +
      '}\n';

    document.head.appendChild(style);
  }

  function swap(parser, sectionHtml, targetSelector, sourceSelector) {
    if (!sectionHtml) return;

    var doc = parser.parseFromString(sectionHtml, 'text/html');
    var source = doc.querySelector(sourceSelector);
    var target = document.querySelector(targetSelector);

    if (source && target) target.innerHTML = source.innerHTML;
  }

  // ---- Sync ---------------------------------------------------------------

  function sync() {
    // No gift configured for this store yet, so don't touch the cart at all.
    if (!GIFT_VARIANT_ID) return;

    if (syncing) {
      syncAgain = true;
      return;
    }

    syncing = true;

    nativeFetch('/cart.js')
      .then(function (response) {
        if (!response.ok) throw new Error('Gift box: /cart.js failed (' + response.status + ')');
        return response.json();
      })
      .then(function (cart) {
        var eligible = eligibleTotal(cart) >= THRESHOLD;
        var giftLines = cart.items.filter(isAutoGift);
        var hasManualGift = cart.items.some(function (item) {
          return isGift(item) && !isAutoGift(item);
        });
        var request = null;

        if (eligible && giftLines.length === 0 && !hasManualGift && !giftUnavailable) {
          request = cartRequest('/cart/add.js', {
            items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: { _auto_gift: 'yes' } }],
          });
        } else if (!eligible && giftLines.length) {
          var updates = {};

          giftLines.forEach(function (item) {
            updates[item.key] = 0;
          });

          request = cartRequest('/cart/update.js', { updates: updates });
        } else if (eligible) {
          request = fixGiftLines(cart);
        }

        return request ? request.then(refreshCartUI) : null;
      })
      .catch(function (error) {
        console.warn(error);
      })
      .finally(function () {
        syncing = false;

        if (syncAgain) {
          syncAgain = false;
          scheduleSync();
        }
      });
  }

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, SYNC_DELAY);
  }

  // ---- Triggers -----------------------------------------------------------

  // Run after the theme changes the cart. Our own requests use nativeFetch,
  // so they never trigger this again.
  window.fetch = function (input) {
    var url = input && input.url ? input.url : input;
    var isCartMutation = typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url);
    var result = nativeFetch.apply(window, arguments);

    if (isCartMutation) {
      result
        .then(function (response) {
          if (response.ok) scheduleSync();
        })
        .catch(function () {});
    }

    return result;
  };

  if (GIFT_VARIANT_ID) addGiftStyles();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') scheduleSync();
  });

  // Back / forward shows the page from the browser cache, so the drawer still
  // has the old items and line numbers. +/- would then change the wrong item.
  window.addEventListener('pageshow', function (event) {
    var navigation = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    var fromHistory = event.persisted || (navigation && navigation.type === 'back_forward');

    if (fromHistory) refreshCartUI();
  });

  document.addEventListener(
    'click',
    function (event) {
      var checkout = event.target.closest(
        'button[name="checkout"], a[href^="/checkout"], .shopify-payment-button__button, ' +
          '.magic-buy-btn, .magic-buy-btn__parent, .magic-cart-btn, .magic-checkout-btn, ' +
          '[data-magic-shopify-btn-ref], [data-magic-branded-btn-ref]'
      );

      if (checkout) sync();
    },
    true
  );
})();
